import { Box, Checkbox, Heading, Input, Text } from '@chakra-ui/react'

import { FileDropzone } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { IStudyCreditOptionRaw, StudyCreditCourseFormat } from '../../../services/StudyCreditRequest/types'
import {
  ABRA_UNIT_LABELS,
  COURSE_FORMAT_LABELS,
  EXTINCT_AWARENESS_TEXT,
  INSTITUTION_LABELS,
  MAX_FILE_SIZE_MB,
  MOEMA_AWARENESS_TEXT
} from './consts'

export interface IStudyFormState {
  institution: '' | 'abra' | 'other_institution' | 'self_taught'
  abraUnit: '' | 'moema_brooklin' | 'extinct_units'
  courseFormat: '' | StudyCreditCourseFormat
  courseId: string
  courseName: string
  institutionName: string
  awarenessConfirmed: boolean
  schoolRecordFiles: File[]
  workFiles: File[]
}

export const emptyStudy = (): IStudyFormState => ({
  institution: '',
  abraUnit: '',
  courseFormat: '',
  courseId: '',
  courseName: '',
  institutionName: '',
  awarenessConfirmed: false,
  schoolRecordFiles: [],
  workFiles: []
})

interface IStudyEntryFieldsProps {
  index: number
  study: IStudyFormState
  onChange: (index: number, study: IStudyFormState) => void
  coursesByFormat: Record<string, IStudyCreditOptionRaw[]>
  onNeedCourses: (format: StudyCreditCourseFormat) => void
  error: (key: string) => string | undefined
  disabled: boolean
}

/**
 * One entry of the "Estudos Realizados Anteriormente" section, with the four
 * conditional paths (ABRA Moema/Brooklin, ABRA unidades extintas, outras
 * instituições and autodidata).
 */
export function StudyEntryFields({
  index,
  study,
  onChange,
  coursesByFormat,
  onNeedCourses,
  error,
  disabled
}: IStudyEntryFieldsProps) {
  const key = (field: string) => `studies.${index}.${field}`

  const isAbra = study.institution === 'abra'
  const isMoema = isAbra && study.abraUnit === 'moema_brooklin'
  const isExtinct = isAbra && study.abraUnit === 'extinct_units'
  const isOtherInstitution = study.institution === 'other_institution'
  const isSelfTaught = study.institution === 'self_taught'

  const update = (changes: Partial<IStudyFormState>) => onChange(index, { ...study, ...changes })

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mt={3}>
      <Heading as="h3" mb={2}>
        Estudo {index + 1}
      </Heading>

      <FormField label="Instituição de Ensino" mt={2} error={error(key('institution'))}>
        <FormSelect
          value={study.institution}
          // Changing the institution resets every downstream answer of the entry.
          onChange={(value) =>
            onChange(index, { ...emptyStudy(), institution: value as IStudyFormState['institution'] })
          }
          options={Object.entries(INSTITUTION_LABELS).map(([value, label]) => ({ value, label }))}
          isDisabled={disabled}
        />
      </FormField>

      {isAbra && (
        <>
          <FormField label="Unidade" mt={4} error={error(key('abra_unit'))}>
            <FormSelect
              isDisabled={disabled}
              value={study.abraUnit}
              onChange={(value) =>
                onChange(index, {
                  ...emptyStudy(),
                  institution: 'abra',
                  abraUnit: value as IStudyFormState['abraUnit']
                })
              }
              options={Object.entries(ABRA_UNIT_LABELS).map(([value, label]) => ({
                value: value,
                label: label
              }))}
              placeholder="Selecione"
            />
          </FormField>
        </>
      )}

      {isMoema && (
        <>
          <FormField
            label="Selecione o formato do curso que você cursou ou está cursando"
            mt={4}
            error={error(key('course_format'))}
          >
            <FormSelect
              isDisabled={disabled}
              value={study.courseFormat}
              onChange={(value) => {
                const format = value as IStudyFormState['courseFormat']
                update({ courseFormat: format, courseId: '' })
                if (format) onNeedCourses(format)
              }}
              options={Object.entries(COURSE_FORMAT_LABELS).map(([value, label]) => ({
                value: value,
                label: label
              }))}
              placeholder="Selecione"
            />
          </FormField>

          {study.courseFormat && (
            <>
              <FormField
                label="Selecione o curso que você cursou ou está cursando"
                mt={4}
                error={error(key('course_id'))}
              >
                <SearchableSelect
                  placeholder="Selecione o curso..."
                  value={study.courseId}
                  onChange={(value) => update({ courseId: value })}
                  options={(coursesByFormat[study.courseFormat] ?? []).map((c) => ({
                    value: String(c.id),
                    label: c.name
                  }))}
                />
              </FormField>
            </>
          )}

          <FormField mt={4} error={error(key('awareness_confirmed'))}>
            <Checkbox
              isChecked={study.awarenessConfirmed}
              onChange={(e) => update({ awarenessConfirmed: e.target.checked })}
              isDisabled={disabled}
              alignItems="flex-start"
            >
              <Text fontSize="sm">{MOEMA_AWARENESS_TEXT}</Text>
            </Checkbox>
          </FormField>
        </>
      )}

      {isExtinct && (
        <>
          <FormField label="Informe o nome do curso que você cursou" mt={4} error={error(key('course_name'))}>
            <Input
              value={study.courseName}
              onChange={(e) => update({ courseName: e.target.value })}
              disabled={disabled}
            />
          </FormField>

          <FileDropzone
            accept="attachment"
            maxSizeMB={MAX_FILE_SIZE_MB}
            mt={4}
            label="Histórico escolar"
            files={study.schoolRecordFiles}
            onChange={(files) => update({ schoolRecordFiles: files })}
            error={error(key('school_record_files'))}
            isDisabled={disabled}
          />

          <FormField mt={4} error={error(key('awareness_confirmed'))}>
            <Checkbox
              isChecked={study.awarenessConfirmed}
              onChange={(e) => update({ awarenessConfirmed: e.target.checked })}
              isDisabled={disabled}
              alignItems="flex-start"
            >
              <Text fontSize="sm">{EXTINCT_AWARENESS_TEXT}</Text>
            </Checkbox>
          </FormField>

          <FileDropzone
            accept="attachment"
            maxSizeMB={MAX_FILE_SIZE_MB}
            mt={4}
            label="Anexe aqui os trabalhos produzidos durante o curso"
            files={study.workFiles}
            onChange={(files) => update({ workFiles: files })}
            error={error(key('work_files'))}
            isDisabled={disabled}
          />
        </>
      )}

      {isOtherInstitution && (
        <>
          <FormField label="Nome da instituição" mt={4} error={error(key('institution_name'))}>
            <Input
              value={study.institutionName}
              onChange={(e) => update({ institutionName: e.target.value })}
              disabled={disabled}
            />
          </FormField>

          <FormField label="Curso" mt={4} error={error(key('course_name'))}>
            <Input
              value={study.courseName}
              onChange={(e) => update({ courseName: e.target.value })}
              disabled={disabled}
            />
          </FormField>

          <FileDropzone
            accept="attachment"
            maxSizeMB={MAX_FILE_SIZE_MB}
            mt={4}
            label="Histórico Escolar/Certificado/Diploma"
            files={study.schoolRecordFiles}
            onChange={(files) => update({ schoolRecordFiles: files })}
            error={error(key('school_record_files'))}
            isDisabled={disabled}
          />

          <FileDropzone
            accept="attachment"
            maxSizeMB={MAX_FILE_SIZE_MB}
            mt={4}
            label="Anexe aqui os trabalhos produzidos durante o curso"
            files={study.workFiles}
            onChange={(files) => update({ workFiles: files })}
            error={error(key('work_files'))}
            isDisabled={disabled}
          />
        </>
      )}

      {isSelfTaught && (
        <FileDropzone
          accept="attachment"
          maxSizeMB={MAX_FILE_SIZE_MB}
          mt={4}
          label="Anexe aqui os trabalhos produzidos"
          files={study.workFiles}
          onChange={(files) => update({ workFiles: files })}
          error={error(key('work_files'))}
          isDisabled={disabled}
        />
      )}
    </Box>
  )
}
